import React, { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useInView } from 'react-intersection-observer';

import { API } from 'core/API';
import { formatDate, generateLocation } from 'core/functions';
import { resolveNextSort } from 'core/functions/sort';
import { TABLE_HEADERS, TABLE_ITEMS_CLASSNAMES } from 'core/constants';

import MenuTable from 'components/Organization/TableMenu';
import Loader from 'shared/components/Loader';
import ResponseFailure from 'shared/components/ResponseFailure';
import PopupConfirmation from 'shared/components/PopupConfirmation';

import TableBody from 'shared/components/Table/TableBody';
import TableHead from 'shared/components/Table/TableHead';
import TableHeadCell from 'shared/components/Table/TableHead/TableHeadCell';
import TableHeadCellSortable from 'shared/components/Table/TableHead/TableHeadCellSortable';
import TableMain from 'shared/components/Table/TableMain';
import TableRow from 'shared/components/Table/TableRow';
import AvatarCell from 'shared/components/Table/CellRenderers/AvatarCell';
import CellDefault from 'shared/components/Table/CellRenderers/CellDefault';
import PostPlaceholder from 'assets/placeholders/Posts.png';

import { POST_POSSIBLE_SORT, PostsResponse, SortDirection } from '@joc/api-gateway/lib/api-client';

import { SUPER_ADMIN_POSTS_QUERY_KEY, useAdminPostsSearch } from './hooks/useAdminPostsSearch';
import PostUpsertPopup from './components/PostUpsertPopup';
import PostRowActions from './components/PostRowActions';

import styles from './SuperAdminPostsTableNew.module.scss';

const SuperAdminPostsTableNew: FC = () => {
	const queryClient = useQueryClient();

	const [search, setSearch] = useState('');
	const [sortBy, setSortBy] = useState<POST_POSSIBLE_SORT | undefined>(undefined);
	const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(undefined);

	const [selectedPost, setSelectedPost] = useState<PostsResponse | undefined>(undefined);
	const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
	const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const [openedActionsPostId, setOpenedActionsPostId] = useState<number | null>(null);

	const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch, error } =
		useAdminPostsSearch({
			search: search || undefined,
			sortBy,
			sortDirection,
		});

	const posts = useMemo(() => data?.pages.flatMap(page => page.records) || [], [data]);

	const { ref: loadMoreRef, inView } = useInView({ threshold: 0, delay: 200 });
	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage().finally();
		}
	}, [inView, hasNextPage, fetchNextPage]);

	const closeMenu = () => {
		setMenuAnchor(null);
		setOpenedActionsPostId(null);
	};

	const openMenu = (postId: number, event: MouseEvent<HTMLButtonElement>) => {
		setOpenedActionsPostId(postId);
		setMenuAnchor(event.currentTarget);
	};

	const onSort = (field: POST_POSSIBLE_SORT, direction?: SortDirection) => {
		const nextSort = resolveNextSort({
			currentSort: sortBy && sortDirection ? { sortBy, sortDir: sortDirection } : undefined,
			nextSortBy: field,
			clickedDirection: direction,
			allowClear: true,
		});

		setSortBy(nextSort?.sortBy);
		setSortDirection(nextSort?.sortDir);
	};

	const onSearchChange = (value: string) => {
		setSearch(value);
	};

	const closePostPopups = () => {
		setIsEditPopupOpen(false);
		setIsDeletePopupOpen(false);
		setSelectedPost(undefined);
	};

	const setDeletePopupVisibility = (value: boolean) => {
		setIsDeletePopupOpen(value);
		if (!value) {
			setSelectedPost(undefined);
		}
	};

	const deletePost = async () => {
		if (!selectedPost?.id) return;
		try {
			await API.deletePost(selectedPost.id, undefined);
			await queryClient.invalidateQueries({ queryKey: [SUPER_ADMIN_POSTS_QUERY_KEY], exact: false });
		} finally {
			closePostPopups();
		}
	};

	const errorMessage =
		(error as any)?.response?.data?.message ||
		(error as any)?.response?.message ||
		(error as any)?.message ||
		'Failed to load posts.';

	return (
		<div className={styles.container}>
			<div className={styles.table}>
				<MenuTable searchChangeHandler={onSearchChange}>
					<div />
				</MenuTable>

				{isError && !isLoading ? (
					<ResponseFailure
						styleTable={true}
						message={errorMessage}
						buttonTitle="Try again"
						buttonClickHandler={refetch}
					/>
				) : !posts.length && isLoading ? (
					<div className={styles.loader}>
						<Loader />
					</div>
				) : !posts.length ? (
					<ResponseFailure styleTable={true} message="No posts found." buttonUnshow={true} />
				) : (
					<TableMain>
						<TableHead callChild="challengeOppotunities">
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.avatar}
								text={TABLE_HEADERS.logo}
								isSortDisabled={true}
							/>
							<TableHeadCellSortable
								title="Title"
								sortBy={POST_POSSIBLE_SORT.Name}
								handleSortChange={onSort}
								sort={sortBy && sortDirection ? { sortBy, sortDir: sortDirection } : undefined}
							/>
							<TableHeadCellSortable
								title="Expiration Date"
								sortBy={POST_POSSIBLE_SORT.ExpirationDate}
								handleSortChange={onSort}
								sort={sortBy && sortDirection ? { sortBy, sortDir: sortDirection } : undefined}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.location}
								sortBy={POST_POSSIBLE_SORT.Address}
								handleSortChange={onSort}
								sort={sortBy && sortDirection ? { sortBy, sortDir: sortDirection } : undefined}
							/>

							<TableHeadCellSortable
								title={TABLE_HEADERS.organization}
								sortBy={POST_POSSIBLE_SORT.Organization}
								handleSortChange={onSort}
								sort={sortBy && sortDirection ? { sortBy, sortDir: sortDirection } : undefined}
							/>

							<TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} />
						</TableHead>

						<TableBody>
							{posts.map(post => {
								return (
									<TableRow key={post.id} callChild="challengeOppotunities">
										{post.imagePath?.trim() ? (
											<AvatarCell
												imagePath={post.imagePath?.trim() || PostPlaceholder}
												parentClassName={TABLE_ITEMS_CLASSNAMES.avatar}
												withPopup={true}
												offerTitle={post.name || 'Post'}
											/>
										) : (
											<div style={{ minHeight: 101, display: 'flex', alignItems: 'center' }}>
												<img
													src={PostPlaceholder}
													alt="Post"
													style={{
														width: 56,
														height: 56,
														borderRadius: '50%',
														objectFit: 'cover',
													}}
												/>
											</div>
										)}
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.chesedName}
											text={post.name || '-'}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.date}
											text={
												post.expirationDate ? formatDate(post.expirationDate) : 'No Expiration'
											}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.location}
											text={post.isVirtual ? 'Virtual' : generateLocation(post.address) || '-'}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.organization}
											text={post.organization?.organizationName || '-'}
										/>
										<PostRowActions
											isOpen={Boolean(menuAnchor) && openedActionsPostId === post.id}
											anchorEl={menuAnchor}
											onMenuOpen={event => openMenu(post.id, event)}
											onMenuClose={closeMenu}
											onEditClick={() => {
												closeMenu();
												setSelectedPost(post);
												setIsEditPopupOpen(true);
											}}
											onDeleteClick={() => {
												closeMenu();
												setSelectedPost(post);
												setIsDeletePopupOpen(true);
											}}
										/>
									</TableRow>
								);
							})}

							{hasNextPage ? <div ref={loadMoreRef} style={{ height: 1, visibility: 'hidden' }} /> : null}

							{isFetchingNextPage ? (
								<div className={styles.loader} aria-hidden={true}>
									<Loader />
								</div>
							) : null}
						</TableBody>
					</TableMain>
				)}
			</div>

			{isEditPopupOpen && selectedPost ? (
				<PostUpsertPopup
					post={selectedPost}
					onClose={() => {
						closePostPopups();
					}}
					onSaved={async () => {
						await queryClient.invalidateQueries({ queryKey: [SUPER_ADMIN_POSTS_QUERY_KEY], exact: false });
						await refetch();

						closePostPopups();
					}}
				/>
			) : null}

			{isDeletePopupOpen && selectedPost ? (
				<PopupConfirmation
					subtitle="Are you sure you want to delete this post?"
					confirmClickHandler={deletePost}
					cancelClickHandler={closePostPopups}
					setIsShowPopup={setDeletePopupVisibility as any}
				/>
			) : null}
		</div>
	);
};

export default SuperAdminPostsTableNew;
